import { useEffect } from "react";
import axios from "axios";

import useHelpers from "../../helpers";
import useComponents from "../../views/components";
import useKeycloak from "../keycloak";

const useInterceptor = (store) => {
  // Helpers
  const { useQuickFunctions } = useHelpers();
  const { useToast } = useQuickFunctions();
  const { error } = useToast();

  // Components
  const { Toast } = useComponents();

  // Keycloak
  const { updateKeycloakToken, keycloakLogout } = useKeycloak();

  const handleRequestSuccess = async (request) => {
    const state = store.getState();

    // Lista de rutas no protegidas
    const unprotectedRoutes = [
      "auth/login",
      "auth/email-forgot-password",
      "auth/reset-password",
    ];

    // Verifica si la solicitud pertenece a una ruta no protegida
    const isUnprotectedRoute = unprotectedRoutes.some((route) =>
      request.url.includes(route)
    );

    // Manejo del loader para ciertas solicitudes
    if (!request.url.includes("/tools/stadistics/copilot")) {
      store.dispatch({ type: "LOADER_ON" });
    }

    // Agrega encabezado de autorización solo si no es una ruta no protegida
    const { authToken, isKeycloakAuth } = state.session;

    // If Keycloak authentication, try to refresh token if needed
    if (!isUnprotectedRoute && isKeycloakAuth && authToken) {
      try {
        const freshToken = await updateKeycloakToken(30); // Refresh if expires in less than 30 seconds
        const tokenToUse = freshToken || authToken;
        request.headers.authorization = `Bearer ${tokenToUse}`;
      } catch (error) {
        console.error('Error refreshing Keycloak token:', error);
        request.headers.authorization = `Bearer ${authToken}`;
      }
    } else if (!isUnprotectedRoute && authToken) {
      request.headers.authorization = `Bearer ${authToken}`;
    }

    // Encabezados comunes
    request.headers["accept"] = "application/json";
    request.headers["time-zone"] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    request.params = {
      ...request.params, // Mantiene los parámetros originales
    };

    return request;
  };

  const handleRequestError = (error) => {
    store.dispatch({ type: "LOADER_OFF" });
    return Promise.reject(error); // Permite que Axios maneje correctamente el error
  };

  const handleResponseSuccess = (response) => {
    store.dispatch({ type: "LOADER_OFF" });
    return response;
  };

  const callErrorToast = (text = "", listOfErrors, id = "") => {
    error(<Toast text={text} listOfErrors={listOfErrors} state={"error"} />, {
      closeOnClick: true,
      toastId: id,
    });
  };

  const handleResponseError = (error) => {
    store.dispatch({ type: "LOADER_OFF" });

    // Check if error is already a response object (has status property)
    const responseObj = error.response || (error.status ? error : null);

    if (!responseObj) {
      callErrorToast("Error de conexión. Verifica tu red.", []);
      return Promise.reject(error);
    }

    const { status, data, config } = responseObj;

    // Lista de rutas que manejan sus propios errores
    const selfHandledRoutes = [
      "auth/login",
      "auth/email-forgot-password",
      "auth/reset-password",
    ];

    // Check if this route handles its own errors
    const isSelfHandled = selfHandledRoutes.some((route) =>
      config?.url?.includes(route)
    );

    switch (status) {
      case 401:
        callErrorToast(data?.message || "Sesión expirada", []);

        // Check if Keycloak auth and logout from Keycloak too
        const state = store.getState();
        if (state.session?.isKeycloakAuth) {
          keycloakLogout();
        }

        store.dispatch({ type: "LOGOUT" });
        window.location.href = "/";
        break;
      case 400:
        return Promise.reject(responseObj);
      case 403:
        // If route handles its own errors, don't show toast
        if (isSelfHandled) {
          return Promise.reject(responseObj);
        }
        callErrorToast(data?.message || "No tienes permisos para realizar esta acción", []);
        return Promise.reject(error);
      default:
        // If route handles its own errors, don't show toast
        if (isSelfHandled) {
          return Promise.reject(responseObj);
        }
        callErrorToast(data?.message || "Ocurrió un error inesperado", []);
        return Promise.reject(error);
    }
  };

  useEffect(() => {
    axios.defaults.baseURL = `${process.env.REACT_APP_API_URL}api/`;
    axios.interceptors.request.use(handleRequestSuccess, handleRequestError);
    axios.interceptors.response.use(handleResponseSuccess, handleResponseError);
  }, []);
};

export default useInterceptor;
